import DOMPurify from "dompurify";

export interface SanitizeOptions {
  isUserGenerated?: boolean;
  allowedTags?: string[];
  allowedAttr?: string[];
  forbiddenTags?: string[];
  forbiddenAttr?: string[];
}

export const defaultSanitizeOptions: SanitizeOptions = {
  allowedTags: [
    "p",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "strong",
    "em",
    "u",
    "br",
    "ul",
    "ol",
    "li",
    "blockquote",
    "a",
  ],
  allowedAttr: ["href"],
  forbiddenAttr: ["style", "onerror", "onload", "onclick", "onmouseover"],
  forbiddenTags: ["script", "iframe", "form", "input", "button", "textarea"],
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * Uses DOMPurify with configurable options
 */
export const sanitizeContent = (
  content: string | null | undefined, // ✅ Allow null/undefined
  options: SanitizeOptions = defaultSanitizeOptions,
): string | null => {
  if (content === null || content === undefined || content === "") {
    return null;
  }

  const sanitizeConfig = {
    ALLOWED_TAGS: options?.allowedTags ?? defaultSanitizeOptions.allowedTags,
    ALLOWED_ATTR: options?.allowedAttr ?? defaultSanitizeOptions.allowedAttr,
    FORBID_TAGS: options?.forbiddenTags ?? defaultSanitizeOptions.forbiddenTags,
    FORBID_ATTR: options?.forbiddenAttr ?? defaultSanitizeOptions.forbiddenAttr,
    ADD_URLS: true,
    WHOLE_DOCUMENT: false,
    ALLOW_COMMENTS: false,
  };

  return DOMPurify.sanitize(content, sanitizeConfig);
};

/**
 * Sanitize plain text (removes all HTML)
 */
export const sanitizePlainText = (
  text: string | null | undefined,
): string | null => {
  if (text === null || text === undefined || text === "") {
    return null;
  }

  return sanitizeContent(text, {
    allowedTags: [],
    allowedAttr: [],
    forbiddenTags: defaultSanitizeOptions.forbiddenTags,
    forbiddenAttr: defaultSanitizeOptions.forbiddenAttr,
  });
};

/**
 * Sanitize text for safe display in attributes
 */
export const sanitizeAttribute = (
  text: string | null | undefined,
): string | null => {
  if (text === null || text === undefined || text === "") {
    return null;
  }

  const sanitized = sanitizePlainText(text);
  return sanitized ? sanitized.replace(/["'<>&]/g, "") : null;
};

/**
 * Sanitize phone number by removing spaces and formatting characters
 */
export const sanitizePhoneNumber = (
  phoneNumber: string | null | undefined,
): string | null => {
  if (!phoneNumber) return null;
  return phoneNumber.replace(/\s/g, "");
};

/**
 * Sanitize a phone object by removing spaces from the number field
 */
export const sanitizePhoneObject = (phone: {
  countryCode: string;
  areaCode: string | null;
  number: string;
  extension: string | null;
}) => {
  return {
    ...phone,
    number: sanitizePhoneNumber(phone.number),
  };
};
